import { utcToLocal } from './event-time';

// Runtime-agnostic UTF-8 byte length (Node + browsers). RFC 5545 folding
// counts octets, not code-points; we previously used Node-only
// `Buffer.byteLength` which prevented bundling this module into the
// frontend.
const utf8Encoder = new TextEncoder();
function utf8ByteLength(s: string): number {
  return utf8Encoder.encode(s).length;
}

export interface IcsEventInput {
  id: string;
  title: string;
  description: string | null;
  venueText: string | null;
  startsAt: Date;
  /** When `false`, ICS emits an all-day VEVENT (`DTSTART;VALUE=DATE:YYYYMMDD`). */
  hasStartTime: boolean;
  endsAt: Date | null;
  organizerUsername: string | null;
  videoUrl: string | null;
  categories: { name: string }[];
}

export interface IcsCityInput {
  /** IANA timezone identifier, e.g. `Asia/Ho_Chi_Minh`. */
  timezone: string;
}

export interface IcsOptions {
  /** Public URL of the event, e.g. `https://upme.app/events/<id>`. */
  canonicalUrl: string;
  /** Override `DTSTAMP`. Defaults to `new Date()` at call time. */
  now?: Date;
}

const CRLF = '\r\n';
const MAX_OCTETS_FIRST = 75;
const MAX_OCTETS_CONT = 74; // 1 octet consumed by the leading space.

/**
 * Render an `Event` as a single-VEVENT RFC 5545 calendar.
 *
 * - Line folding is performed on UTF-8 *octet* length (not code-point length),
 *   never inside a multi-byte code point.
 * - Property values are escaped per RFC 5545 §3.3.11 *before* folding.
 * - All line breaks use CRLF; UTF-8 without BOM.
 * - `TZID=<city.timezone>` is emitted without a VTIMEZONE block — supported by
 *   Google / Apple / Outlook for IANA names.
 */
export function formatIcs(event: IcsEventInput, city: IcsCityInput, options: IcsOptions): string {
  const now = options.now ?? new Date();

  const dtstamp = formatUtcStamp(now);

  const lines: string[] = [];
  lines.push('BEGIN:VCALENDAR');
  lines.push('VERSION:2.0');
  lines.push('PRODID:-//upme//events//EN');
  lines.push('CALSCALE:GREGORIAN');
  lines.push('BEGIN:VEVENT');
  lines.push(`UID:${event.id}@upme.app`);
  lines.push(`DTSTAMP:${dtstamp}`);
  if (event.hasStartTime) {
    const dtstart = formatLocalStamp(event.startsAt, city.timezone);
    lines.push(`DTSTART;TZID=${city.timezone}:${dtstart}`);
    if (event.endsAt !== null) {
      const dtend = formatLocalStamp(event.endsAt, city.timezone);
      lines.push(`DTEND;TZID=${city.timezone}:${dtend}`);
    } else {
      lines.push('DURATION:PT1H');
    }
  } else {
    // All-day event: per RFC 5545 §3.6.1, DTSTART;VALUE=DATE without TZID,
    // DTEND is exclusive (next day for single-day events).
    const startDate = formatLocalDate(event.startsAt, city.timezone);
    lines.push(`DTSTART;VALUE=DATE:${startDate}`);
    if (event.endsAt !== null) {
      // endsAt is the inclusive end (city tz); ICS DTEND is exclusive, so add 1 day.
      const endDate = formatLocalDate(addOneDay(event.endsAt), city.timezone);
      lines.push(`DTEND;VALUE=DATE:${endDate}`);
    } else {
      const endDate = formatLocalDate(addOneDay(event.startsAt), city.timezone);
      lines.push(`DTEND;VALUE=DATE:${endDate}`);
    }
  }
  lines.push(`SUMMARY:${escapeText(event.title)}`);

  const description = buildDescription(event);
  if (description !== null) {
    lines.push(`DESCRIPTION:${description}`);
  }

  if (event.venueText) {
    lines.push(`LOCATION:${escapeText(event.venueText)}`);
  }
  lines.push(`URL:${options.canonicalUrl}`);

  if (event.categories.length > 0) {
    const cats = event.categories.map((c) => escapeText(c.name)).join(',');
    lines.push(`CATEGORIES:${cats}`);
  }

  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.map(foldLine).join(CRLF) + CRLF;
}

function buildDescription(event: IcsEventInput): string | null {
  const segments: string[] = [];
  if (event.description !== null && event.description.length > 0) {
    segments.push(event.description);
  }
  if (event.organizerUsername !== null && event.organizerUsername.length > 0) {
    segments.push(`Organizer: @${event.organizerUsername}`);
  }
  if (event.videoUrl !== null && event.videoUrl.length > 0) {
    segments.push(`Video: ${event.videoUrl}`);
  }
  if (segments.length === 0) return null;
  // Join sections with a blank line (rendered as literal `\n\n` per RFC 5545).
  return segments.map(escapeText).join('\\n\\n');
}

/**
 * RFC 5545 §3.3.11 text escape. Order matters: backslash must be escaped
 * first so we do not re-escape the backslashes we are about to introduce.
 */
function escapeText(input: string): string {
  return input
    .replace(/\\/g, '\\\\')
    .replace(/\r\n/g, '\\n')
    .replace(/\r/g, '\\n')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

/**
 * Fold a single content line per RFC 5545 §3.1: first segment ≤ 75 octets,
 * continuations ≤ 74 octets prefixed with a single space (the space itself
 * counts toward the 75-octet line limit).
 *
 * Octets are UTF-8 bytes, not characters. We iterate by code point so we
 * never split inside a multi-byte sequence.
 */
function foldLine(line: string): string {
  const lineBytes = utf8ByteLength(line);
  if (lineBytes <= MAX_OCTETS_FIRST) return line;

  const codepoints = Array.from(line);
  const out: string[] = [];
  let current = '';
  let currentBytes = 0;
  let limit = MAX_OCTETS_FIRST;
  let isFirst = true;

  for (const cp of codepoints) {
    const cpBytes = utf8ByteLength(cp);
    if (currentBytes + cpBytes > limit) {
      out.push(current);
      current = cp;
      currentBytes = cpBytes;
      isFirst = false;
      limit = MAX_OCTETS_CONT;
      continue;
    }
    current += cp;
    currentBytes += cpBytes;
  }
  if (current.length > 0 || isFirst) {
    out.push(current);
  }

  return out.map((seg, i) => (i === 0 ? seg : ` ${seg}`)).join(CRLF);
}

function formatUtcStamp(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = pad2(d.getUTCMonth() + 1);
  const day = pad2(d.getUTCDate());
  const h = pad2(d.getUTCHours());
  const min = pad2(d.getUTCMinutes());
  const s = pad2(d.getUTCSeconds());
  return `${y}${m}${day}T${h}${min}${s}Z`;
}

function formatLocalStamp(utc: Date, tz: string): string {
  const local = utcToLocal(utc, tz);
  return `${local.date.replace(/-/g, '')}T${local.time.replace(':', '')}00`;
}

function formatLocalDate(utc: Date, tz: string): string {
  const local = utcToLocal(utc, tz);
  return local.date.replace(/-/g, '');
}

function addOneDay(d: Date): Date {
  return new Date(d.getTime() + 24 * 60 * 60 * 1000);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
